import {Group, InputElement, Textarea} from "@chakra-ui/react";
import type {TextareaProps} from "@chakra-ui/react";
import {Field, FieldProps} from "./ui/field";
import {CommitTextProps, useCommitText} from "../hooks/commitText";
import {InputSpinner} from "./InputSpinner";
import {memo, ReactNode, useCallback} from "react";
import {AutoResizingTextarea, AutoResizingTextareaProps} from "./AutoResizingTextarea";

type CommitTextareaProps = CommitTextProps &
  TextareaProps & {
    formControlProps?: FieldProps;
    inputGroupProps?: React.ComponentProps<typeof Group>;
    showSpinner?: boolean;
    autoResizingProps?: AutoResizingTextareaProps;
    // Element pinned to the bottom-right inside the field box. Reserve room with `paddingBottom`
    // so it doesn't overlap the text.
    endOverlay?: ReactNode;
  };

const CommitTextarea = memo(
  ({
    onCommit,
    value,
    onChange: propsOnChange,
    preCommit,
    inputGroupProps,
    formControlProps,
    showSpinner = true,
    autoResizingProps,
    endOverlay,
    ...props
  }: CommitTextareaProps) => {
    const {currentValue, inputRef, onChange, committing} = useCommitText(value, onCommit, preCommit);

    const innerOnChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        propsOnChange?.(e);
        onChange();
      },
      [onChange, propsOnChange],
    );

    const baseProps = {ref: inputRef, value: currentValue, onChange: innerOnChange, ...props};

    return (
      <Field invalid={!!committing.lastError} errorText={committing.lastError?.toString()} {...formControlProps}>
        <Group h="full" w="full" {...inputGroupProps}>
          {autoResizingProps ? (
            <AutoResizingTextarea {...baseProps} {...autoResizingProps} />
          ) : (
            <Textarea {...baseProps} />
          )}
          {showSpinner && (
            <InputElement placement="end" alignItems="end" py={2} pointerEvents="none">
              <InputSpinner inProgress={committing.inProgress} />
            </InputElement>
          )}
          {endOverlay && (
            <InputElement placement="end" alignItems="end" px={2} py={2} pointerEvents="auto">
              {endOverlay}
            </InputElement>
          )}
        </Group>
      </Field>
    );
  },
);

export default CommitTextarea;
